import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import InteractiveMap, {
  ESRI_SATELLITE_TILE_URL,
  ESRI_SATELLITE_TILE_ATTRIBUTION,
} from 'components/InteractiveMap';
import BrazilStatesLayer from 'components/Meteorology/Agro/BrazilStatesLayer';
import Icon from 'components/DesignSystem/Icon';
import { store } from 'redux/store';
import {
  setCreateAlert,
  setEditAlert,
  selectMeteorologyFieldsGroupLocationCreateAlert,
  selectMeteorologyFieldsGroupLocationEditAlert,
} from 'redux/reducers/meteorologyFieldsGroupLocationFeedbackReducer';
import useLocationMarkers from 'hooks/Meteorology/Fields/TrackingForecast/Register/useLocationMarkers';
import useGroups from 'hooks/Meteorology/Fields/TrackingForecast/Register/useGroups';
import useLocations from 'hooks/Meteorology/Fields/TrackingForecast/Register/useLocations';
import useCities from 'hooks/Meteorology/Fields/TrackingForecast/Register/useCities';
import useSaveGroup from 'hooks/Meteorology/Fields/TrackingForecast/Register/useSaveGroup';
import useUpdateGroup from 'hooks/Meteorology/Fields/TrackingForecast/Register/useUpdateGroup';
import useUpdateLocation from 'hooks/Meteorology/Fields/TrackingForecast/Register/useUpdateLocation';
import useDeleteGroup from 'hooks/Meteorology/Fields/TrackingForecast/Register/useDeleteGroup';
import useDeleteLocation from 'hooks/Meteorology/Fields/TrackingForecast/Register/useDeleteLocation';
import useFieldsSaveLocationParams from 'hooks/Meteorology/Fields/TrackingForecast/Register/RegisterForm/useFieldsSaveLocationParams';
import { cssVar } from 'constants/theme';

import RegisterSheet from 'components/Meteorology/Shared/RegisterMobile/RegisterSheet';
import EditSheet from 'components/Meteorology/Shared/RegisterMobile/EditSheet';

import 'components/Meteorology/Shared/RegisterMobile/index.scss';

export default function RegisterMobile() {
  const markers = useLocationMarkers();
  const { groups } = useGroups();
  const { locations } = useLocations();
  const { cities } = useCities();
  const { saveGroup } = useSaveGroup();
  const { saveLocationParams } = useFieldsSaveLocationParams();
  const { updateGroup } = useUpdateGroup();
  const { updateLocation } = useUpdateLocation();
  const { deleteGroup } = useDeleteGroup();
  const { deleteLocation } = useDeleteLocation();

  const alertRegister = useSelector(selectMeteorologyFieldsGroupLocationCreateAlert);
  const alertEdit = useSelector(selectMeteorologyFieldsGroupLocationEditAlert);

  const [registerOpen, setRegisterOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  const closeRegister = useCallback(() => {
    setRegisterOpen(false);
    store.dispatch(setCreateAlert(undefined));
  }, []);
  const closeEdit = useCallback(() => {
    setEditOpen(false);
    store.dispatch(setEditAlert(undefined));
  }, []);

  const groupCount = groups?.length ?? 0;
  const locationCount = locations?.length ?? 0;
  const summaryLabel = `${groupCount} grupo${groupCount === 1 ? '' : 's'} · ${locationCount} ${locationCount === 1 ? 'local' : 'locais'}`;

  return (
    <div className="register-mobile tracking-forecast-register-mobile">
      <div className="register-mobile-map-card">
        <button
          type="button"
          className="register-mobile-edit-button"
          onClick={() => setEditOpen(true)}
        >
          <Icon iconType="edit" color={cssVar('text-strong')} size="small" />
          Editar
        </button>
        <InteractiveMap
          markers={markers}
          minZoom={3}
          tileUrl={ESRI_SATELLITE_TILE_URL}
          tileAttribution={ESRI_SATELLITE_TILE_ATTRIBUTION}
        >
          <BrazilStatesLayer />
        </InteractiveMap>
      </div>

      <div className="register-mobile-summary-card">
        <div className="register-mobile-summary-header">
          <span className="register-mobile-summary-title">Grupos e locais</span>
          <span className="register-mobile-summary-count">{summaryLabel}</span>
        </div>
        {groups?.map((group) => {
          const groupLocations = locations?.filter((l) => l.groupId === group.id) || [];
          const chipLabel = groupLocations.length === 1
            ? groupLocations[0].name
            : `${groupLocations.length} locais`;
          return (
            <div key={group.id} className="register-mobile-summary-row">
              <span className="register-mobile-summary-icon">
                <Icon iconType="list" color={cssVar('text-strong')} size="small" />
              </span>
              <div className="register-mobile-summary-info">
                <span className="register-mobile-summary-name">{group.name}</span>
                <span className="register-mobile-summary-sub">
                  {`Grupo · ${groupLocations.length} ${groupLocations.length === 1 ? 'local' : 'locais'}`}
                </span>
              </div>
              <span className="register-mobile-summary-chip">{chipLabel}</span>
            </div>
          );
        })}
        {groupCount === 0 && (
          <p className="register-mobile-summary-empty">Nenhum grupo cadastrado ainda.</p>
        )}
      </div>

      <button
        type="button"
        className="register-mobile-fab"
        onClick={() => setRegisterOpen(true)}
      >
        <Icon iconType="add" color={cssVar('text-on-accent')} size="small" />
        Cadastrar
      </button>

      <RegisterSheet
        open={registerOpen}
        dismiss={closeRegister}
        idPrefix="tf"
        alert={alertRegister}
        groups={groups}
        cities={cities}
        saveGroup={saveGroup}
        saveLocationParams={saveLocationParams}
      />
      <EditSheet
        open={editOpen}
        dismiss={closeEdit}
        alert={alertEdit}
        groups={groups}
        locations={locations}
        updateGroup={updateGroup}
        updateLocation={updateLocation}
        deleteGroup={deleteGroup}
        deleteLocation={deleteLocation}
      />
    </div>
  );
}
